import { isNil } from 'lodash';
import { IBuilderInputPayload } from '../../../../redux/slices/oldBuilder';
import {
  DateKindKey,
  ExpressionModeKey,
  ExpressionQuerySettingKey,
  IExecuteBasicInput,
  IExpressionInput,
  IQueryExecuteInput,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { parseCohortRowsToCohortRefInput } from '../../../../utils/builder/cohorts';
import { prepareQueryExecuteMeasureInput } from '../../../../utils/builder/measures/inputs';
import { parseFilterToInput } from '../../../../utils/filterFields';
import { assertCondition } from '../../../../utils/types';
import {
  generateBucketDefInput,
  parseEventConditionToInput,
  toDatePickerInput,
} from '../builderUtils';
import {
  llmChatMessageToInput,
  prepareBreakdownInput,
} from '../validateExecutionInput';

const prepareQueryExecuteInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IQueryExecuteInput => {
  const { dateInput, binning } = state;

  const {
    activeSchemaId,
    sourceId,
    resultFilter,
    compoundMeasureId,
    additionalSettings,
    selectedOptions,
    globalCohorts,
    measures,
    measureFormula,
    comparisonConfig,
    llmContext,
  } = state;

  assertCondition(selectedOptions?.type === QueryType.Expression);

  const {
    dateKind,
    mode,
    subject,
    triggerSubject,
    timeUnit,
    includeMissingDates,
  } = selectedOptions;

  const basicInput: IExecuteBasicInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    unit: timeUnit,
    schemaId: activeSchemaId,
    sourceId: sourceId ?? null,
    dateKind,
    breakdown: prepareBreakdownInput(state),
    cohortRefs: parseCohortRowsToCohortRefInput(globalCohorts, activeSchemaId),
    samplePercentage: null,
    filter: null,
    timeWindow: null,
    comparisonConfig: null,
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
  };

  if (state.filter) {
    basicInput.filter = parseFilterToInput(state.filter, state.activeSchemaId);
  }

  // handle comparison config
  if (mode === ExpressionModeKey.Query && comparisonConfig) {
    const { offset, timeUnit } = comparisonConfig;

    basicInput.comparisonConfig = {
      offset,
      timeUnit,
    };
  }

  const { measure, measureProperties } = prepareQueryExecuteMeasureInput(
    {
      measures,
      resultFilter,
      compoundMeasureId,
      measureFormula,
    },
    activeSchemaId,
  );

  const expression: IExpressionInput = {
    name: null,
    measure,
    measureProperties,
    settings: additionalSettings as ExpressionQuerySettingKey[],
    mode,
    triggerSubject: null,
    triggerCondition: null,
    subject: null,
    subjectBucketDefInput: null,
    includeMissingDates,
    llmChatMessages: llmContext.previousMessages.map(llmChatMessageToInput),
  };

  if (
    additionalSettings.includes(ExpressionQuerySettingKey.Sampling) &&
    state.formulaSamplingRate !== null
  ) {
    basicInput.samplePercentage = state.formulaSamplingRate;
  }

  // handle Trigger events in Impact analysis mode
  if (mode === ExpressionModeKey.Impact && state?.trigger) {
    expression.triggerSubject = triggerSubject;
    expression.triggerCondition = parseEventConditionToInput(
      state.trigger,
      activeSchemaId,
    );
  }

  if (!isNil(state.timeWindow) && dateKind === DateKindKey.Install) {
    basicInput.timeWindow = state.timeWindow;
  }

  // handle Histogram binning in Histogram mode
  if (mode === ExpressionModeKey.Histogram) {
    const bucketDefInput = generateBucketDefInput(
      binning,
      subject,
      activeSchemaId,
    );

    expression.subject = subject;
    expression.subjectBucketDefInput = bucketDefInput;
  }

  return {
    basicInput,
    expression,
  };
};

export default prepareQueryExecuteInput;
